import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';

export const facilitiesApi = createApi({
  reducerPath: 'facilitiesApi',
  baseQuery,
  tagTypes: ['Facilities', 'FacilityBids'],
  endpoints: (builder) => ({
    getFacilitiesList: builder.query({
      query: ({
        portfolioId,
        page = 1,
        size = 10,
        sortBy = 'facilityName',
        sortDir = 'asc',
        search = '',
        showDisabled,
      }) => {
        const params = new URLSearchParams({ page, size, sortBy, sortDir });
        if (search) params.set('search', search);
        if (showDisabled !== undefined) params.set('showDisabled', showDisabled);
        return `/facilities/portfolio/${portfolioId}?${params}`;
      },
      keepUnusedDataFor: 0,
      transformResponse: (response) => {
        const items = response.result || response || [];
        const data = items.map((facility) => ({
          facilityId: facility.facilityId,
          portfolioId: facility.portfolioId,
          facilityName: facility.facilityName,
          alternateName: facility.alternateName || '',
          status: facility.isActive ? 'enabled' : 'disabled',
          tags: facility.tags || [],
          clv: facility.clv,
        }));
        return {
          data,
          pagination: response.pagination || null,
        };
      },
      providesTags: (_result, _error, { portfolioId }) => [{ type: 'Facilities', id: portfolioId }],
    }),

    getFacilityBids: builder.query({
      query: ({
        portfolioId,
        page = 1,
        size = 10,
        sortBy = 'facilityName',
        sortDir = 'asc',
        search = '',
        tags = [],
      }) => {
        const params = new URLSearchParams({ page, size, sortBy, sortDir });
        if (search) params.set('search', search);
        if (tags.length > 0) params.set('tags', tags.join(','));
        return `/facilities/portfolio/${portfolioId}/bids?${params}`;
      },
      transformResponse: (response) => {
        const data = response.result || response || [];
        const items = data.map((item, index) => {
          const { facility, cityBidOpportunity, zipBidOpportunity, newBid } = item;
          return {
            key: facility?.id || String(index + 1),
            facilityId: facility?.id,
            facilityName: facility?.alternateName || facility?.name || 'N/A',
            occupancy: facility?.facilityPhysicalOccupancy || null,
            currentCityRanking: cityBidOpportunity?.rank ?? 'N/A',
            currentZipRanking: zipBidOpportunity?.rank ?? 'N/A',
            bid: cityBidOpportunity?.bid ?? zipBidOpportunity?.bid ?? 0,
            maxBid: facility?.maxBid ?? 0,
            minBid: facility?.minBid ?? 0,
            optimizedBid: facility?.optimizedBid ?? 0,
            bidDelta: facility?.bidDelta ?? 0,
            newCityRanking: facility?.newCityRanking ?? 'N/A',
            newZipRanking: facility?.newZipRanking ?? 'N/A',
            cityRank: cityBidOpportunity?.rank ?? 69,
            zipRank: zipBidOpportunity?.rank ?? 0,
            cityBidForRanks: cityBidOpportunity?.bidForRanks ?? [],
            zipBidForRanks: zipBidOpportunity?.bidForRanks ?? [],
            clv: facility?.clv,
            maxBidRecommendation: newBid?.maxBidRecommendation,
            tags: facility?.tags || [],
            lastAction: facility?.lastAction,
          };
        });
        return {
          data: items,
          pagination: response.pagination || null,
        };
      },
      providesTags: (_result, _error, { portfolioId }) => [
        { type: 'FacilityBids', id: portfolioId },
      ],
    }),

    updateFacility: builder.mutation({
      query: ({ facilityId, ...data }) => ({
        url: `/facilities/${facilityId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Facilities', 'FacilityBids'],
    }),

    updateFacilityMaxBid: builder.mutation({
      query: ({ facilityId, maxBid }) => ({
        url: `/facilities/${facilityId}`,
        method: 'PUT',
        body: { maxBid },
      }),
      invalidatesTags: ['FacilityBids'],
    }),

    updateAllFacilityMaxBids: builder.mutation({
      query: ({ portfolioId, maxBid }) => ({
        url: `/facilities/portfolio/${portfolioId}/maxbid`,
        method: 'PUT',
        body: { maxBid },
      }),
      invalidatesTags: ['FacilityBids'],
    }),

    runOptimizer: builder.mutation({
      query: (portfolioId) => ({
        url: `/sparefoot/runOptimizer/${portfolioId}`,
        method: 'POST',
      }),
      invalidatesTags: ['FacilityBids'],
    }),

    submitBids: builder.mutation({
      query: (portfolioId) => ({
        url: `/sparefoot/submitBids/${portfolioId}`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetFacilitiesListQuery,
  useGetFacilityBidsQuery,
  useUpdateFacilityMutation,
  useUpdateFacilityMaxBidMutation,
  useUpdateAllFacilityMaxBidsMutation,
  useRunOptimizerMutation,
  useSubmitBidsMutation,
} = facilitiesApi;
